import type { ReactNode } from 'react';

import { Estado, Etiqueta, Painel, Tempo, sistemaDe, type Carga } from '../../comum';
import { APTIDAO, CICLO, ROTULO_VERSAO, type ComViabilidade, type Pagina } from './tipos';

/**
 * A tabela do parque.
 *
 * ⚠️ A seleção é guardada por **id**, nunca por índice: a lista se recarrega
 * sozinha e a posição de uma máquina muda entre uma carga e outra. Selecionar
 * por posição já significaria abrir chamado para a máquina errada.
 */
export function TabelaDeDispositivos({
  lista,
  selecao,
  setSelecao,
  aoAbrir,
  exportar,
  podeSelecionar,
}: {
  readonly lista: Carga<Pagina>;
  readonly selecao: ReadonlySet<string>;
  readonly setSelecao: (s: ReadonlySet<string>) => void;
  readonly aoAbrir: (id: string) => void;
  readonly exportar: () => void;
  readonly podeSelecionar: boolean;
}): ReactNode {
  return (
    <Painel>
      <Estado carga={lista}>
        {lista.dados !== null && (
          <>
            <div className="linha-contagem">
              <p className="suave contagem">
                {lista.dados.itens.length} de {lista.dados.total}
                {lista.dados.filtradoNaMemoria === true && (
                  <span title="A aptidão cruza TPM, Secure Boot, build e memória — não é uma coluna do banco, então o filtro roda sobre as primeiras 500 máquinas.">
                    {' '}
                    · recorte por aptidão
                  </span>
                )}
              </p>
              {lista.dados.itens.length > 0 && (
                <button
                  className="link"
                  onClick={exportar}
                  title="Baixa em CSV exatamente o que está na tabela"
                >
                  Exportar CSV ↓
                </button>
              )}
            </div>
            <div className="rolagem">
              <table className="densa">
                <thead>
                  <tr>
                    {podeSelecionar && (
                      <th style={{ width: 28 }}>
                        <input
                          type="checkbox"
                          aria-label="Selecionar todos os visíveis"
                          checked={
                            lista.dados.itens.length > 0 &&
                            lista.dados.itens.every((d) => selecao.has(d.id))
                          }
                          onChange={(e) =>
                            setSelecao(
                              e.target.checked
                                ? new Set(lista.dados?.itens.map((d) => d.id) ?? [])
                                : new Set(),
                            )
                          }
                        />
                      </th>
                    )}
                    <th>Nome</th>
                    <th>Sistema</th>
                    <th>Windows 11</th>
                    <th>Ciclo</th>
                    <th>Modelo</th>
                    <th>Usuário</th>
                    <th>Último inventário</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.dados.itens.map((d) => {
                    // Etiqueta em TODA linha, com o tom certo. Antes, quem não
                    // era Windows perdia a etiqueta e virava texto cru.
                    const so = sistemaDe(d);
                    return (
                      <tr key={d.id} className="clicavel" onClick={() => aoAbrir(d.id)}>
                        {/* O clique na caixa não abre a ficha. */}
                        {podeSelecionar && (
                          <td onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              aria-label={`Selecionar ${d.nome}`}
                              checked={selecao.has(d.id)}
                              onChange={(e) => {
                                const nova = new Set(selecao);
                                if (e.target.checked) nova.add(d.id);
                                else nova.delete(d.id);
                                setSelecao(nova);
                              }}
                            />
                          </td>
                        )}
                        <td className="forte">{d.nome}</td>
                        <td>
                          <Etiqueta
                            tom={so.tom}
                            {...(so.titulo === undefined ? {} : { titulo: so.titulo })}
                          >
                            {so.texto}
                          </Etiqueta>
                        </td>
                        <td>
                          {d.viabilidade !== undefined && APTIDAO[d.viabilidade] !== undefined && (
                            <Etiqueta
                              tom={APTIDAO[d.viabilidade]?.tom ?? 'neutro'}
                              {...(d.viabilidadeResumo === undefined
                                ? {}
                                : { titulo: d.viabilidadeResumo })}
                            >
                              {APTIDAO[d.viabilidade]?.rotulo ?? d.viabilidade}
                            </Etiqueta>
                          )}
                        </td>
                        <td>
                          {d.recomendacao !== undefined &&
                            CICLO[d.recomendacao] !== undefined &&
                            d.recomendacao !== 'manter' && (
                              <Etiqueta
                                tom={CICLO[d.recomendacao]?.tom ?? 'neutro'}
                                {...(d.recomendacaoMotivo === undefined
                                  ? {}
                                  : { titulo: d.recomendacaoMotivo })}
                              >
                                {CICLO[d.recomendacao]?.rotulo ?? d.recomendacao}
                              </Etiqueta>
                            )}
                        </td>
                        <td className="suave">
                          {[d.hwFabricante, d.hwModelo].filter(Boolean).join(' ') || '—'}
                        </td>
                        <td className="suave">{d.usuarioLogado ?? '—'}</td>
                        <td>
                          <Tempo iso={d.ultimoInventario} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Estado>
    </Painel>
  );
}
